import { useCallback, useEffect, useRef, useState } from "react"
import { cn } from "@lib/utils/helpers"
import styles from "../../styles/applicationDetail.module.css"
import type { CreditReportHtmlSectionProps } from '../../types/tabs'

function readIframeDocumentHeight(iframe: HTMLIFrameElement): number | null {
  try {
    const doc = iframe.contentDocument
    if (!doc?.documentElement && !doc?.body) return null
    const root = doc.documentElement
    const body = doc.body
    const h = Math.max(
      root?.scrollHeight ?? 0,
      body?.scrollHeight ?? 0,
      root?.offsetHeight ?? 0,
      body?.offsetHeight ?? 0,
    )
    return h > 32 ? h : null
  } catch {
    return null
  }
}

/** Embedded bureau HTML report iframe with same-origin height sync (Angular resizeIframe). */
export function CreditReportHtmlSection({
  htmlReportUrl,
  iframeRevision = "",
  bureauActions,
}: CreditReportHtmlSectionProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [iframeSizedToContent, setIframeSizedToContent] = useState(false)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset the iframe-sized flag when the report source changes
    setIframeSizedToContent(false)
  }, [htmlReportUrl, iframeRevision])

  const syncIframeHeight = useCallback(() => {
    const el = iframeRef.current
    if (!el) return
    const inner = readIframeDocumentHeight(el)
    if (inner != null) {
      const padded = Math.min(inner + 32, 24_000)
      el.style.height = `${padded}px`
      el.style.maxHeight = "none"
      setIframeSizedToContent(true)
      return
    }
    el.style.height = ""
    el.style.maxHeight = ""
    setIframeSizedToContent(false)
  }, [])

  const handleIframeLoad = useCallback(() => {
    syncIframeHeight()
    ;[40, 120, 400, 1000, 1800, 2500].forEach((ms) => {
      window.setTimeout(() => syncIframeHeight(), ms)
    })
  }, [syncIframeHeight])

  const {
    showResubmit,
    resubmitLabel,
    onResubmit,
    resubmitDisabled,
    showOpen,
    onOpenCreditReport,
    openCreditReportDisabled,
    openCreditReportTooltip,
    openingReport,
  } = bureauActions

  const showActionBar = showResubmit || showOpen

  return (
    <div className={styles.creditReportHtmlSection} data-testid="application-detail-credit-report-section">
      {showActionBar ? (
      <div
        className={styles.creditReportHtmlActions}
        data-testid="application-detail-credit-report-actions"
      >
        {showOpen ? (
        <button
          type="button"
          className={styles.creditReportHtmlActionButton}
          data-testid="application-detail-credit-report-open-button"
          onClick={() => void onOpenCreditReport()}
          disabled={openCreditReportDisabled}
          title={openCreditReportTooltip}
        >
          {openingReport ? "Working…" : "Open Credit Report"}
        </button>
        ) : null}
        {showResubmit ? (
          <button
            type="button"
            className={styles.creditReportHtmlActionButton}
            data-testid="application-detail-credit-report-resubmit-button"
            onClick={onResubmit}
            disabled={resubmitDisabled}
          >
            {resubmitLabel}
          </button>
        ) : null}
      </div>
      ) : null}

      <div
        className={styles.creditReportHtmlIframeContainer}
        data-testid="application-detail-credit-report-iframe-container"
      >
        <iframe
          key={`${htmlReportUrl}|${iframeRevision}`}
          ref={iframeRef}
          src={htmlReportUrl}
          className={cn(
            styles.creditReportHtmlIframe,
            !iframeSizedToContent && styles.creditReportHtmlIframePending,
          )}
          title="Bureau HTML report"
          data-testid="application-detail-credit-report-iframe"
          onLoad={handleIframeLoad}
        />
      </div>
    </div>
  )
}
